import {
  CheckCircleOutlined,
  CloseCircleFilled,
  GlobalOutlined,
  MedicineBoxOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Button, Col, Row, Tooltip, Typography } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { createUseStyles, useTheme } from 'react-jss'
import { Link } from 'react-router-dom'
import { capitalize } from 'lodash'
import api from '../../api'
import { useAsync } from '../../hooks'
import { useIsAdmin, useCurrentUser } from '../../redux/slices/session'
import { useSelectedPractice } from '../../context/PracticeContext'
import { toPath } from '../../routes'
import {
  MOBILE_MEDIA_QUERY,
  TABLET_MEDIA_QUERY,
} from '../../lib/responsive'
import { ListError } from '../Common'
import dayjs from '../../lib/datetime'
import { usePracticeOptions } from '../Forms/PracticeSelect'
import ChartTableSwitch from './ChartTableSwitch'
import ConnectivityRates from './ConnectivityRates'
import ConnectivityRatesChart from './ConnectivityRatesChart'
import DashboardSkeleton from './DashboardSkeleton'
import DevicesSummary from './DevicesSummary'
import NeedsAttention from './NeedsAttention'
import PatientCreationStats from './PatientCreationStats'
import PreAuthorizationStats from './PreAuthorizationStats'
import ReportsStats from './ReportsStats'

const { Title } = Typography

const useStyles = createUseStyles({
  root: {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 12,
  },
  heroTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  heroMain: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  welcome: {
    fontWeight: '400 !important',
    color: '#0f172a',
    fontSize: 32,
    lineHeight: 1.15,
    // rendered as h1 for the page heading; kill its default margins so the
    // hero keeps its own spacing
    margin: 0,
    textWrap: 'balance',
    letterSpacing: '-0.02em',
    '& span': {
      fontWeight: 600,
    },
    [MOBILE_MEDIA_QUERY]: {
      fontSize: '1.9em',
    },
  },
  welcomeSubtitle: {
    // #64748b is 4.36:1 on the #f5f5f5 ground; one step darker clears AA at 5.11:1
    color: '#5b697f',
    fontSize: 13,
    marginTop: 4,
    fontVariantNumeric: 'tabular-nums',
  },
  scopeBanner: {
    marginTop: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px 10px 12px',
    borderRadius: 8, // antd borderRadiusLG
    border: '1px solid',
    width: '100%',
    boxSizing: 'border-box',
    transitionProperty: 'background-color, border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
  },
  scopeBannerAll: {
    background: '#f8fafc',
    borderColor: '#e2e8f0',
    color: '#0f172a',
  },
  scopeBannerSingle: {
    color: '#fff',
    // Transparente y no `none`: la variante de todas las practicas si tiene borde,
    // y sin el 1px las dos versiones del banner no medirian lo mismo.
    borderColor: 'transparent',
  },
  scopeBannerIcon: {
    width: 36,
    height: 36,
    borderRadius: 4, // antd borderRadiusSM
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    flexShrink: 0,
  },
  scopeBannerIconAll: {
    background: '#fff',
    color: '#475569',
    border: '1px solid #e2e8f0',
  },
  scopeBannerIconSingle: {
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
  },
  scopeBannerBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  scopeKicker: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    // No opacity: at 0.7 the white variant lands at 3.15:1 on the brand banner.
    // Full-strength inherited color passes on both banner variants.
  },
  scopeName: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.005em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  scopeClear: {
    // Darken instead of lighten: white text on the lightened pill was 3.31:1.
    // Over the brand banner this reads the same but clears AA at ~6.2:1.
    background: 'rgba(0,0,0,0.18)',
    border: 'none',
    padding: '5px 10px 5px 8px',
    borderRadius: 6, // antd borderRadius
    cursor: 'pointer',
    color: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    transitionProperty: 'background-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
    '&:hover': { background: 'rgba(0,0,0,0.28)' },
    '&:active': { transform: 'scale(0.96)' },
  },
  scopeHint: {
    fontSize: 12,
    // #94a3b8 was ~2.5:1 on the light banner
    color: '#5b697f',
    flexShrink: 0,
  },
  heroStats: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
  },
  heroStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px 6px 8px',
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 6, // antd borderRadius
    textDecoration: 'none',
    color: 'inherit',
    transitionProperty: 'border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
    '&:hover': {
      borderColor: '#d9d9d9',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0) scale(0.97)',
    },
  },
  heroStatIcon: {
    width: 24,
    height: 24,
    borderRadius: 4, // antd borderRadiusSM
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    flexShrink: 0,
  },
  heroStatValue: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    color: '#0f172a',
  },
  heroStatLabel: {
    fontSize: 12,
    color: '#64748b',
  },
  card: {
    background: '#fff',
    padding: '16px 20px',
    // Mismo tratamiento que Common/SectionCard: el fondo de pagina ya es gris,
    // asi que el borde de 1px separa sin necesidad de sombra.
    borderRadius: 8, // antd borderRadiusLG
    border: '1px solid #f0f0f0', // antd colorBorderSecondary
    position: 'relative',
    // Allow grid items to shrink below their content width, otherwise the
    // ApexCharts canvases keep their rendered width and overflow on mobile.
    minWidth: 0,
    [MOBILE_MEDIA_QUERY]: {
      padding: '10px 12px',
    },
  },
  grid: {
    display: 'grid',
    gap: 14,
    marginBottom: 20,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    // Two charts side by side leave each too narrow to read here.
    [TABLET_MEDIA_QUERY]: {
      gridTemplateColumns: '1fr',
    },
    [MOBILE_MEDIA_QUERY]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  cardTitle: {
    margin: '0 !important',
    fontSize: '16px !important',
    fontWeight: '600 !important',
    color: '#0f172a !important',
    letterSpacing: '-0.005em',
  },
})

function greetingFor(date = new Date()) {
  const hour = date.getHours()
  if (hour < 5) return 'Still up'
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

function trendFromRates(rates) {
  if (!rates || rates.length < 2) return null
  const latest = rates[rates.length - 1]?.conectivityRate
  const previous = rates[rates.length - 2]?.conectivityRate
  if (latest == null || previous == null) return null
  const delta = Math.round((latest - previous) * 1000) / 10
  return {
    latestPct: Math.round(latest * 100),
    deltaPct: delta,
  }
}

function HeroStat({ to, icon, iconBg, iconColor, value, label }) {
  const classes = useStyles()
  return (
    <Link to={to} className={classes.heroStat} title={label}>
      <div
        className={classes.heroStatIcon}
        style={{ background: iconBg, color: iconColor }}
      >
        {icon}
      </div>
      <span className={classes.heroStatValue}>{value}</span>
      <span className={classes.heroStatLabel}>{label}</span>
    </Link>
  )
}

HeroStat.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  iconBg: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
}

export default function Dashboard() {
  const { brandPrimary } = useTheme()
  const classes = useStyles()
  const isAdmin = useIsAdmin()
  const user = useCurrentUser()
  const { selectedPracticeId, setSelectedPracticeId } = useSelectedPractice()
  const practiceId = isAdmin ? selectedPracticeId : undefined
  const fetchData = useCallback(() => api.dashboard.get({ practiceId }), [practiceId])
  const { value: stats, pending, error, execute } = useAsync(fetchData)
  const [connectivityMode, setConnectivityMode] = useState('chart')

  const { options: practiceOptions } = usePracticeOptions()
  const selectedPracticeName = useMemo(() => {
    if (!isAdmin) return user?.practice?.name || null
    if (!selectedPracticeId) return null
    return practiceOptions.find(o => o.value === String(selectedPracticeId))?.label || null
  }, [isAdmin, selectedPracticeId, practiceOptions, user])
  const isAllPractices = isAdmin && !selectedPracticeId

  const today = useMemo(
    () => dayjs().format('dddd, MMMM D, YYYY'),
    [],
  )
  const greeting = useMemo(() => greetingFor(), [])

  const connectivitySeries = useMemo(
    () => (stats?.connectivityRates ? [...stats.connectivityRates].reverse() : []),
    [stats?.connectivityRates],
  )
  const connectivityTrend = useMemo(
    () => trendFromRates(connectivitySeries),
    [connectivitySeries],
  )

  if (pending) return <DashboardSkeleton isAdmin={isAdmin} />
  if (error) {
    return (
      <ListError
        description={error.message}
        message="There was an error loading dashboard data"
      />
    )
  }

  return (
    <div className={classes.root}>
      <style>{`
        .ant-layout-content {
          background: transparent !important;
        }
      `}</style>
      <div className={classes.hero}>
        <div className={classes.heroTop}>
          <div className={classes.heroMain}>
            <h1 className={classes.welcome}>
              {greeting}{' '}
              <span style={{ color: brandPrimary }}>
                {capitalize(user.firstName)}
              </span>
            </h1>
            <div className={classes.welcomeSubtitle}>
              {today} — here&apos;s your overview for today
            </div>
          </div>
          <div className={classes.heroStats}>
            <HeroStat
              to={toPath.patients()}
              icon={<TeamOutlined />}
              iconBg={`${brandPrimary}14`}
              iconColor={brandPrimary}
              value={stats.totalPatients.toLocaleString()}
              label="patients"
            />
            {isAdmin && isAllPractices && (
              <HeroStat
                to={toPath.practices()}
                icon={<MedicineBoxOutlined />}
                iconBg="#7c3aed14"
                iconColor="#7c3aed"
                value={stats.totalPractices.toLocaleString()}
                label="practices"
              />
            )}
            {!isAdmin && connectivityTrend && (
              <HeroStat
                to={toPath.patients()}
                icon={<CheckCircleOutlined />}
                iconBg="#22c55e14"
                iconColor="#22c55e"
                value={`${connectivityTrend.latestPct}%`}
                label="connectivity"
              />
            )}
            <Tooltip title="Refresh data">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => execute()}
                aria-label="Refresh dashboard data"
              />
            </Tooltip>
          </div>
        </div>
        {isAdmin && (
          isAllPractices ? (
            <div
              className={`${classes.scopeBanner} ${classes.scopeBannerAll}`}
              role="status"
              aria-label="Showing data aggregated across all practices"
            >
              <span
                className={`${classes.scopeBannerIcon} ${classes.scopeBannerIconAll}`}
              >
                <GlobalOutlined />
              </span>
              <div className={classes.scopeBannerBody}>
                <span className={classes.scopeKicker}>Showing</span>
                <span className={classes.scopeName}>
                  All practices
                  {stats?.totalPractices != null && (
                    // explicit muted colour: 0.55 opacity landed at 3.94:1
                    <span style={{ color: '#5b697f', fontWeight: 500 }}>
                      {' '}· aggregated across {stats.totalPractices.toLocaleString()} practices
                    </span>
                  )}
                </span>
              </div>
              <span className={classes.scopeHint}>
                Pick one in the header to drill in ↑
              </span>
            </div>
          ) : (
            <div
              className={`${classes.scopeBanner} ${classes.scopeBannerSingle}`}
              role="status"
              aria-label={`Showing data for ${selectedPracticeName || 'a single practice'}`}
              style={{ background: brandPrimary }}
            >
              <span
                className={`${classes.scopeBannerIcon} ${classes.scopeBannerIconSingle}`}
              >
                <MedicineBoxOutlined />
              </span>
              <div className={classes.scopeBannerBody}>
                <span className={classes.scopeKicker}>Filtered to one practice</span>
                <span
                  className={classes.scopeName}
                  title={selectedPracticeName || ''}
                >
                  {selectedPracticeName || 'Your practice'}
                </span>
              </div>
              <Tooltip title="Show all practices">
                <button
                  type="button"
                  className={classes.scopeClear}
                  onClick={() => setSelectedPracticeId(null)}
                >
                  <CloseCircleFilled />
                  <span>Show all</span>
                </button>
              </Tooltip>
            </div>
          )
        )}
        {!isAdmin && selectedPracticeName && (
          <div
            className={`${classes.scopeBanner} ${classes.scopeBannerSingle}`}
            role="status"
            aria-label={`Showing data for ${selectedPracticeName}`}
            style={{ background: brandPrimary }}
          >
            <span
              className={`${classes.scopeBannerIcon} ${classes.scopeBannerIconSingle}`}
            >
              <MedicineBoxOutlined />
            </span>
            <div className={classes.scopeBannerBody}>
              <span className={classes.scopeKicker}>Your practice</span>
              <span className={classes.scopeName} title={selectedPracticeName}>
                {selectedPracticeName}
              </span>
            </div>
          </div>
        )}
      </div>

      <NeedsAttention
        preAuthorizationStats={stats?.preAuthorizationStats}
      />

      {(() => {
        const isScoped = !!selectedPracticeName && !isAllPractices
        const cardStyle = isScoped
          ? { borderColor: `${brandPrimary}33` }
          : undefined
        return (
          <div className={classes.grid}>
            <div className={classes.card} style={cardStyle}>
              {stats?.devices && <DevicesSummary devicesStats={stats.devices} />}
            </div>

            <div className={classes.card} style={cardStyle}>
              {stats?.patientCreationStats && (
                <PatientCreationStats stats={stats.patientCreationStats} />
              )}
            </div>

            {stats?.connectivityRates && (
              <div className={classes.card} style={cardStyle}>
                <Row
                  justify="space-between"
                  type="flex"
                  align="middle"
                  className={classes.cardHeader}
                >
                  <Col>
                    {/* level 2 for heading order; cardTitle pins the visual size */}
                    <Title level={2} className={classes.cardTitle}>
                      Connectivity rates
                    </Title>
                  </Col>
                  <Col>
                    <ChartTableSwitch
                      onChange={setConnectivityMode}
                      viewMode={connectivityMode}
                    />
                  </Col>
                </Row>

                {connectivityMode === 'chart' && (
                  <ConnectivityRatesChart rates={connectivitySeries} />
                )}
                {connectivityMode === 'table' && (
                  <ConnectivityRates rates={connectivitySeries} />
                )}
              </div>
            )}

            {stats?.reportsStats && isAdmin && (
              <div className={classes.card} style={cardStyle}>
                <ReportsStats reports={stats.reportsStats} />
              </div>
            )}

            {stats?.preAuthorizationStats && isAdmin && (
              <div className={classes.card} style={cardStyle}>
                <PreAuthorizationStats stats={stats.preAuthorizationStats} />
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}
